import { Dispatch, SetStateAction, useState } from 'react';
import {
  JSONObject,
  GridInputValue,
  LayoutCanvas,
  LayoutDefinition,
  LayoutObject,
} from '../../../types/types';
import { HandlerRequestContext } from '../../../api/types/types';
import { useMatrixDefault } from '../../../api/hooks/useMatrixDefault';
import { useGenerateMatrix } from '../../../api/hooks/useGenerateMatrix';
import { resolveTemplate } from '../../../utils/json/resolveTemplate';
import { createLayoutRequestBody } from '../../../utils/layouts/createLayoutRequestBody';
import { buildMatrixObjects } from '../../../utils/layouts/buildMatrixObjects';
import { applyLayoutObjectDefaults } from '../../../utils/layouts/applyLayoutObjectDefaults';
import { getLayoutObjectInheritedStyle } from '../../../utils/layouts/getLayoutObjectInheritedStyle';
import { createLayoutObjectKey } from '../../../utils/layouts/createLayoutObjectKey';
import { getUnplacedMatrixObjects } from '../../../utils/layouts/getUnplacedMatrixObjects';
import { getMatrixOverlappingObjects } from '../../../utils/layouts/getMatrixOverlappingObjects';
import { LAYOUT_MATRIX_SKIP_EXISTING_FIELD } from '../../../constants';

type UseLayoutMatrixParams = {
  definition: LayoutDefinition | undefined;
  canvas: LayoutCanvas;
  isFixedMode: boolean;
  template: LayoutObject | null;
  objects: LayoutObject[];
  setObjects: Dispatch<SetStateAction<LayoutObject[]>>;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
  getApiResolveData: () => HandlerRequestContext;
};

type UseLayoutMatrix = {
  isMatrixSupported: boolean;
  isMatrixDialogOpen: boolean;
  matrixFormData: JSONObject | null;
  openMatrixDialog: () => void;
  closeMatrixDialog: () => void;
  seedMatrixFormData: (values: JSONObject) => void;
  patchMatrixFormData: (name: string, value: GridInputValue) => void;
  fetchMatrixDefaults: () => Promise<JSONObject | null>;
  generateMatrix: (
    matrixValues: JSONObject,
    clearExisting?: boolean
  ) => Promise<boolean>;
};

// Owns the "generate matrix" feature: the dialog state with its form values and
// both matrix api methods (defaults fetch + generate). The generated objects are
// appended to the editor list the provider owns.
export const useLayoutMatrix = ({
  definition,
  canvas,
  isFixedMode,
  template,
  objects,
  setObjects,
  setSelectedIndex,
  getApiResolveData,
}: UseLayoutMatrixParams): UseLayoutMatrix => {
  const [isMatrixDialogOpen, setIsMatrixDialogOpen] = useState(false);
  const [matrixFormData, setMatrixFormData] = useState<JSONObject | null>(null);

  const {
    fetch: fetchMatrixDefault,
    detailedFetch: detailedFetchMatrixDefault,
  } = useMatrixDefault();
  const { generate, detailedGenerate } = useGenerateMatrix();

  // Matrix generation: available when the definition carries both
  // the dialog form and the generate method.
  const isMatrixSupported =
    !!definition?.matrixForm && !!definition?.apiMethods.matrixGenerate;

  // Defaults pre-filling the "generate matrix" dialog, from apiMethods.matrixDefault.
  // No method in the definition → null (the dialog starts empty).
  const fetchMatrixDefaults = async (): Promise<JSONObject | null> => {
    const matrixDefault = definition?.apiMethods.matrixDefault;

    if (!matrixDefault) {
      return null;
    }

    const resolveData = getApiResolveData();

    if (typeof matrixDefault === 'string') {
      const { url } = await resolveTemplate(
        { url: matrixDefault },
        resolveData
      );
      return (await fetchMatrixDefault(url)) ?? null;
    }

    return (
      (await detailedFetchMatrixDefault(matrixDefault, resolveData)) ?? null
    );
  };

  // POSTs the matrixForm values, turns the returned data-only objects into a
  // placed rows × columns grid and appends it to the canvas. The appended
  // objects have no baseline counterpart (dirty/new) — persisted only by the
  // subsequent standard save. With clearExisting the objects lying under the
  // generated grid are removed first (the user chose to delete the overlapping
  // originals — the rest of the canvas survives); the removals persist by the
  // same save diff.
  const generateMatrix = async (
    matrixValues: JSONObject,
    clearExisting = false
  ): Promise<boolean> => {
    const matrixGenerate = definition?.apiMethods.matrixGenerate;

    // Same no-op guards as addObject: these modes don't allow new objects.
    if (!matrixGenerate || canvas.lockItems || isFixedMode) {
      return false;
    }

    const resolveData = getApiResolveData();
    const postValues = createLayoutRequestBody(
      matrixValues,
      definition?.matrixForm,
      'POST'
    );

    let dataObjects: LayoutObject[] | undefined;

    if (typeof matrixGenerate === 'string') {
      const { url } = await resolveTemplate(
        { url: matrixGenerate },
        resolveData
      );
      dataObjects = await generate(url, postValues);
    } else {
      dataObjects = await detailedGenerate(
        matrixGenerate,
        resolveData,
        postValues
      );
    }

    if (!dataObjects) {
      return false;
    }

    // Generated buttons inherit the template style the same way addObject
    // does — but only the keys the matrixForm doesn't expose as inputs (those
    // are the user's to set in the dialog).
    const matrixFieldNames = new Set(
      (definition?.matrixForm?.items ?? []).map((item) => item.name)
    );
    const inheritedStyle = Object.fromEntries(
      Object.entries(getLayoutObjectInheritedStyle(template)).filter(
        ([key]) => !matrixFieldNames.has(key)
      )
    ) as LayoutObject;

    // clearExisting: only the objects lying under the generated grid go away.
    const overlappingObjects = clearExisting
      ? new Set(getMatrixOverlappingObjects(matrixValues, objects))
      : null;
    const existingObjects = overlappingObjects
      ? objects.filter((object) => !overlappingObjects.has(object))
      : objects;

    // skipExisting: only the editor knows the current buttons (unsaved adds and
    // deletes included), so the already placed items are dropped here, not on
    // the BE. Cleared originals are no longer "existing" — skipped against the
    // survivors only.
    const objectsToPlace =
      matrixValues[LAYOUT_MATRIX_SKIP_EXISTING_FIELD] === true
        ? getUnplacedMatrixObjects(dataObjects, existingObjects)
        : dataObjects;

    const built = buildMatrixObjects(
      matrixValues,
      objectsToPlace,
      inheritedStyle
    ).map((object) => ({
      ...applyLayoutObjectDefaults(object, definition?.objectForm),
      feKey: createLayoutObjectKey(),
    }));

    setObjects((current) => [
      ...(overlappingObjects
        ? current.filter((object) => !overlappingObjects.has(object))
        : current),
      ...built,
    ]);
    // Removals shift the indexes — the selection could land on a different
    // object (or past the end); the selected original may be gone entirely.
    if (overlappingObjects && overlappingObjects.size > 0) {
      setSelectedIndex(null);
    }

    return true;
  };

  const openMatrixDialog = (): void => {
    if (!definition?.matrixForm) {
      return;
    }

    setIsMatrixDialogOpen(true);
  };

  // Closing throws the dialog values away — the next open re-fetches the defaults.
  const closeMatrixDialog = (): void => {
    setIsMatrixDialogOpen(false);
    setMatrixFormData(null);
  };

  // Initial dialog values (the matrixDefault response); replaces the whole object.
  const seedMatrixFormData = (values: JSONObject): void => {
    setMatrixFormData(values);
  };

  // Single dialog field edit (user input or a picked fullscreen reference).
  const patchMatrixFormData = (name: string, value: GridInputValue): void => {
    setMatrixFormData(
      (current) => ({ ...(current ?? {}), [name]: value }) as JSONObject
    );
  };

  return {
    isMatrixSupported,
    isMatrixDialogOpen,
    matrixFormData,
    openMatrixDialog,
    closeMatrixDialog,
    seedMatrixFormData,
    patchMatrixFormData,
    fetchMatrixDefaults,
    generateMatrix,
  };
};
